import { ColorSwatch, Group, InfiniteTable } from '@/components/_common';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import StatusTag from '@/components/_common/StatusTag';
import { useDirectSales } from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import {
  DirectSale,
  DirectSaleStatusEnum,
  ListDirectSaleQuery,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import Authorities from '@/types/Authority';
import { StatusColor } from '@/utils/enum/statusEnum';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import formatterPrice from '@/utils/number/formatterPrice';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { getTableScrollX } from '@/utils/table';
import { Divider, Space, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { CheckSquare, XSquare } from 'react-feather';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

interface Props {
  handleRemoveClick: (record: DirectSale) => void;
  queryParams?: ListDirectSaleQuery;
}
const DirectSaleList: React.FC<Props> = ({
  handleRemoveClick,
  queryParams,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useDirectSales(queryParams);

  const directSales = flatInfiniteData(data);

  function handleRedirectFinancial(directSaleId: number) {
    if (
      !hasPermissions([
        Authorities.MOVIMENTATION_SALE_EDIT,
        Authorities.MOVIMENTATION_SALE_GENERATE_FINANCIAL,
      ])
    ) {
      return null;
    }

    history.replace(
      `/app/movements/directSale/${directSaleId}/financialDirectSale`,
    );
  }

  const columns: ColumnsType<DirectSale> = [
    {
      title: intl.formatMessage({
        id: 'directSale.columns.table.tag',
      }),
      ellipsis: true,
      width: 70,
      render: (_, row) =>
        !!row.saleTag && (
          <Group position="center">
            <Tooltip
              title={row.saleTag.name?.toUpperCase()}
              color={row.saleTag.color}
              placement="right"
            >
              <div>
                <ColorSwatch backgroundColor={row.saleTag.color} />
              </div>
            </Tooltip>
          </Group>
        ),
    },
    {
      title: intl.formatMessage({
        id: 'directSale.columns.table.company',
      }),
      dataIndex: 'company',
      ellipsis: true,
      width: 240,
    },
    {
      dataIndex: 'saleCode',
      title: intl.formatMessage({ id: 'directSale.columns.table.code' }),
      width: 140,
    },
    {
      title: intl.formatMessage({
        id: 'directSale.columns.table.customer',
      }),
      dataIndex: 'customer',
      ellipsis: true,
      width: 180,
    },
    {
      dataIndex: 'status',
      title: intl.formatMessage({ id: 'directSale.columns.table.status' }),
      width: 130,
      render: (value: DirectSaleStatusEnum) => (
        <StatusTag
          color={StatusColor[value]}
          label={intl.formatMessage({ id: `directSale.${value}` })}
        />
      ),
    },
    {
      title: intl.formatMessage({
        id: `directSale.columns.table.dateOfSale`,
      }),
      dataIndex: 'saleDate',
      render: (value) =>
        value ? moment.utc(value).format('DD/MM/YYYY HH:mm') : '-',
      width: 170,
    },
    {
      dataIndex: 'saleValue',
      title: intl.formatMessage({
        id: 'directSale.columns.table.saleValue',
      }),
      width: 130,
      render: (value: number) => {
        return <>{formatterPrice(value || 0)}</>;
      },
    },
    {
      dataIndex: 'generatedFinancial',
      title: 'Financeiro',
      width: 90,
      align: 'center',
      render: (generatedFinancial, row) => {
        if (generatedFinancial) {
          return (
            <Tooltip title="Financeiro gerado">
              <CheckSquare
                color="green"
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={() => handleRedirectFinancial(row.id)}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Financeiro não gerado">
            <XSquare
              color="red"
              size={20}
              style={{ cursor: 'pointer' }}
              onClick={() => handleRedirectFinancial(row.id)}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'actions',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <EditIcon
            redirect={`/app/movements/directSale/${record.id}`}
            disabled={!hasPermissions([Authorities.MOVIMENTATION_SALE_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={
              (record.status !== DirectSaleStatusEnum.OPEN &&
                (record.status !== DirectSaleStatusEnum.CANCELED ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_SALE_DELETE_CANCELED,
                  ]))) ||
              !hasPermissions([Authorities.MOVIMENTATION_SALE_DELETE])
            }
            onClick={() => handleRemoveClick(record)}
          />
        </Space>
      ),
    },
  ];
  return (
    <InfiniteTable<DirectSale>
      size="small"
      loading={isFetching}
      rowKey={(row) => row.id}
      dataSource={directSales}
      columns={columns}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
};

export default DirectSaleList;
